import React from 'react';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import { InfoIcon, WarningIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';

interface VersionBannerProps {
  currentVersionNumber: number;
  latestVersionNumber: number;
  status: 'draft' | 'finalized';
  onSwitchToLatest?: () => void;
}

export function VersionBanner({
  currentVersionNumber,
  latestVersionNumber,
  status,
  onSwitchToLatest,
}: VersionBannerProps) {
  const isViewingOldVersion = currentVersionNumber < latestVersionNumber;
  const isDraft = status === 'draft';

  if (!isViewingOldVersion && !isDraft) {
    return null;
  }

  if (isViewingOldVersion) {
    return (
      <AlertContainer className="mb-4 border-blue-500 bg-blue-50">
        <InfoIcon className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">
          Viewing Previous Version
        </AlertTitle>
        <AlertDescription className="flex items-center justify-between text-blue-800">
          <span>
            You are viewing Version {currentVersionNumber}.
            {onSwitchToLatest && (
              <span className="ml-1">
                Switch to Version {latestVersionNumber} to see the latest
                version.
              </span>
            )}
          </span>
          {onSwitchToLatest && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSwitchToLatest}
              className="ml-4 border-blue-600 text-blue-700 hover:bg-blue-100"
            >
              Switch to Latest
            </Button>
          )}
        </AlertDescription>
      </AlertContainer>
    );
  }

  if (isDraft) {
    return (
      <AlertContainer className="mb-4 border-yellow-500 bg-yellow-50">
        <WarningIcon className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-900">Draft Version</AlertTitle>
        <AlertDescription className="text-yellow-800">
          This is a draft version. Finalize this version to lock the rules and
          accept CSV uploads.
        </AlertDescription>
      </AlertContainer>
    );
  }

  return null;
}
